import React, { useState, useRef, useEffect } from "react";

const greetingMessage = {
  from: "bot",
  text: "Hi! I can help you track an order's shipment. What's your order number?",
};

function describeOrderStatus(shipmentState) {
  switch (shipmentState) {
    case "shipped":
      return "Good news, your order has shipped!";
    case "partial":
      return "Part of your order has shipped.";
    case "backorder":
      return "Your order has items on backorder.";
    case "canceled":
      return "This order was canceled.";
    default:
      return "Your order hasn't shipped yet, we'll email you tracking information as soon as it does.";
  }
}

const shipmentStatusLabels = {
  pre_transit: "Label created",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  available_for_pickup: "Available for pickup",
  return_to_sender: "Being returned to sender",
  cancelled: "Canceled",
  failure: "There was a delivery issue",
};

const statusesStillOnTheWay = ["pre_transit", "in_transit", "out_for_delivery"];

function describeShipment(shipment) {
  const tracking = shipment.tracking
    ? " Tracking number: " + shipment.tracking + "."
    : "";

  const label = shipmentStatusLabels[shipment.status];

  if (label) {
    const location = shipment.location
      ? ", last scanned in " + shipment.location
      : "";
    const eta =
      shipment.estimated_delivery &&
      statusesStillOnTheWay.includes(shipment.status)
        ? " Estimated delivery " + shipment.estimated_delivery + "."
        : "";
    return "• " + label + location + "." + eta + tracking;
  }

  const when = shipment.shipped_on ? " on " + shipment.shipped_on : "";
  const via = shipment.carrier ? " via " + shipment.carrier : "";
  return "• Shipped" + when + via + "." + tracking;
}

function buildTrackingSummary(order) {
  const shippedShipments = (order.shipments || []).filter(
    (shipment) => shipment.state === "shipped",
  );
  return [describeOrderStatus(order.shipment_state)]
    .concat(shippedShipments.map(describeShipment))
    .join("\n");
}

function trackingLinks(order) {
  return (order.shipments || [])
    .filter((shipment) => shipment.state === "shipped" && shipment.tracking_url)
    .map((shipment) => ({
      label: "Track " + (shipment.carrier || "package"),
      url: shipment.tracking_url,
      carrier: shipment.carrier,
    }));
}

function trackLookup(lookupResult) {
  gtag("event", "chatbot_order_lookup", { lookup_result: lookupResult });
}

export default function CustomerHelperBotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("ask_number");
  const [orderNumberInput, setOrderNumberInput] = useState("");
  const [messages, setMessages] = useState([greetingMessage]);
  const messageLogRef = useRef(null);
  const launcherRef = useRef(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && messageLogRef.current) {
      messageLogRef.current.scrollTop = messageLogRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isOpen && wasOpen.current && launcherRef.current) {
      launcherRef.current.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  const addBotMessage = (text) =>
    setMessages((prev) => [...prev, { from: "bot", text }]);

  const lookUpOrder = (orderNumber) => {
    const csrfToken = document
      .querySelector("meta[name='csrf-token']")
      .getAttribute("content");

    fetch("/react/track_order", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ order_number: orderNumber }),
    })
      .then((response) =>
        response.json().then((data) => ({ ok: response.ok, data })),
      )
      .then(({ ok, data }) => {
        if (ok) {
          trackLookup("found");
          setStep("result");
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: buildTrackingSummary(data),
              links: trackingLinks(data),
            },
          ]);
        } else {
          trackLookup("not_found");
          setStep("not_found");
          addBotMessage(
            data.error || "I couldn't find that order number on your account.",
          );
        }
      })
      .catch(() => {
        trackLookup("error");
        setStep("error");
        addBotMessage(
          "Something went wrong looking that up. Please try again in a moment.",
        );
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = orderNumberInput.trim();
    if (!value || step !== "ask_number") {
      return;
    }

    setOrderNumberInput("");
    setStep("loading");
    setMessages((prev) => [
      ...prev,
      { from: "user", text: value },
      { from: "bot", text: "Looking that up…" },
    ]);
    lookUpOrder(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const open = () => {
    gtag("event", "chatbot_open");
    setIsOpen(true);
  };

  const restart = () => {
    setStep("ask_number");
    setOrderNumberInput("");
    setMessages([greetingMessage]);
  };

  if (!isOpen) {
    return (
      <button
        ref={launcherRef}
        type="button"
        onClick={open}
        className="btn btn-tubedepot chat-widget-launcher"
      >
        Track my order
      </button>
    );
  }

  const lookupFinished =
    step === "result" || step === "not_found" || step === "error";

  return (
    <div
      className="chat-widget-panel"
      role="dialog"
      aria-label="Order tracking"
      onKeyDown={handleKeyDown}
    >
      <div className="chat-widget-header">
        <span className="chat-widget-title">Track your order</span>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          aria-label="Close"
          className="chat-widget-close"
        >
          &times;
        </button>
      </div>

      <div
        ref={messageLogRef}
        className="chat-widget-log"
        role="log"
        aria-live="polite"
      >
        {messages.map((message, index) => (
          <div
            key={index}
            className={"chat-widget-row chat-widget-row--" + message.from}
          >
            <span className="chat-widget-bubble">
              <span className="chat-widget-sr-only">
                {message.from === "user" ? "You: " : "2B: "}
              </span>
              {message.text}
              {(message.links || []).map((link, linkIndex) => (
                <a
                  key={linkIndex}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chat-widget-link"
                  onClick={() =>
                    gtag("event", "chatbot_tracking_link_click", {
                      carrier: link.carrier || "unknown",
                    })
                  }
                >
                  {link.label}
                </a>
              ))}
            </span>
          </div>
        ))}
      </div>

      <div className="chat-widget-footer">
        {step === "ask_number" && (
          <form onSubmit={handleSubmit} className="chat-widget-form">
            <input
              type="text"
              value={orderNumberInput}
              onChange={(event) => setOrderNumberInput(event.target.value)}
              placeholder="Order number"
              aria-label="Order number"
              className="chat-widget-input"
              autoFocus
            />
            <button
              type="submit"
              className="btn btn-tubedepot chat-widget-send"
            >
              Send
            </button>
          </form>
        )}
        {step === "loading" && (
          <span className="chat-widget-loading">Looking that up…</span>
        )}
        {lookupFinished && (
          <button
            type="button"
            onClick={restart}
            className="btn btn-tubedepot chat-widget-restart"
          >
            Track another order
          </button>
        )}
      </div>
    </div>
  );
}
